import React, { useEffect, useState } from "react";
import axios from "axios";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import SpinLoading from "components/Loading/SpinLoading.js";
import intl from "react-intl-universal";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import MaterialTable from "@material-table/core";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";

import {
  createTheme,
  MuiThemeProvider,
} from "@material-ui/core/styles";

import Reading from "spectral-core/models/Reading";

import {
  useRootSelector,
  useRootDispatch,
} from "hooks/reduxHooks";

import { logoutAction } from "store/user/user.actions";

import { useHistory } from "react-router-dom";

import {
  RunModelsBatch,
  UpdateReading,
  UpdateResults,
} from "spectral-core/api";



interface Props {
  open: boolean;
  onClose: () => void;
  reading: Reading | null;
  models: any[];
  modelsLoading: boolean;
}

export default function ReadingDetailsModal({
    open,
    onClose,
    reading,
    models,
    modelsLoading,
  }: Props) {
    
  const [selectedTab, setSelectedTab] = useState(0);

  const user = useRootSelector((state) => state.user.currentUser?.name);

  const PlantThirdLabels: Record<number, string> = {
    0: "Superior",
    1: "Médio",
    2: "Baixeiro",  
    3: "Todos",
    4: "Médio Superior"
  };

  const cultureLabels = {
  0: "Algodão",
  1: "Café",
  2: "Cana (Colmo)",
  3: "Milho",
  4: "Soja",
  5: "Demo",
  6: "Solo",
  7: "Outros",
  8: "Eucalipto",
  9: "Cana (Folha)"
};

const sugarcaneTypeLabels: Record<number, string> = {
  0: "Colmo",
  1: "Folha",
};
  
  const accessToken = useRootSelector(
    (state) => state.user.currentToken?.accessToken
  );

  const [recalculating, setRecalculating] = useState(false);
  const [updatingReading, setUpdatingReading] = useState(false);

  const [recalculatedReading, setRecalculatedReading] =
    useState<Reading | null>(null);

  const [selectedModel, setSelectedModel] = useState("");

  const [editedReading, setEditedReading] = useState<Reading | null>(null);

  useEffect(() => {
    if (reading) {
      setEditedReading({
        ...reading,
        name: isAverageReading(reading)
          ? getEditableAverageName(reading.name)
          : reading.name,
      });
    }
  }, [reading]);

  const getModelCultureType = (model: any): number | null => {
    try {
      const modelInfo = JSON.parse(model.model);

      return modelInfo?.CultureType ?? null;
    } catch {
      return null;
    }
  };

  const selectedCulture = reading?.culture;

  const filteredModels = React.useMemo(() => {
    return models
      .filter((model, index, self) => {
        const cultureType = getModelCultureType(model);

        const isCompatibleCulture =
          selectedCulture === 5 ||
          cultureType === selectedCulture;

        return (
          model.applicationName &&
          isCompatibleCulture &&
          index ===
            self.findIndex(
              (m) =>
                m.applicationName ===
                model.applicationName
            )
        );
      })
      .sort((a, b) =>
        a.applicationName.localeCompare(
          b.applicationName
        )
      );
  }, [models, selectedCulture]);

  const getPlantThirdLabel = (
    plantThird?: string | number | null
  ) => {
    if (plantThird == null) return "-";

    return PlantThirdLabels[Number(plantThird)] ?? "-";
  };

  const generalInformation = [
    { label: "Nome da Amostra", value: reading?.name },
    { label: "Código da Amostra", value: reading?.sampleCode },
    { label: "Cliente", value: reading?.customerName },

    { label: "Usuário", value: reading?.userName },
    { label: "Cultura", value: reading?.culture },
    { label: "Cultivar", value: reading?.cultivate },

    { label: "Estádio Fenológico", value: reading?.growthStage },
    { label: "Terço da planta", value: getPlantThirdLabel(reading?.plantThird) },
    { label: "Talhão", value: reading?.plot },

    { label: "Identificador de campo", value: reading?.fieldIdentifier },
    { label: "Aplicação", value: reading?.type },
    { label: "Data e hora", value: reading?.dateTime },
  ];

  const [editingField, setEditingField] = useState<string | null>(null);

  const EditableField = ({
  label,
  field,
  value,
  onChange,
}: {
  label: string;
  field: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 4,
      }}
    >
      <strong>{label}</strong>

      <IconButton
        size="small"
        onClick={() =>
          setEditingField(
            editingField === field ? null : field
          )
        }
      >
        {editingField === field ? (
          <CheckIcon fontSize="small" />
        ) : (
          <EditIcon fontSize="small" />
        )}
      </IconButton>
    </div>

    {editingField === field ? (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <div>{value || "-"}</div>
    )}
  </>
);

  const desiredResultOrder = [
  "N",
  "P",
  "K",
  "Ca",
  "Mg",
  "S",
  "B",
  "Cu",
  "Fe",
  "Mn",
  "Zn",
];

const handlePreviewRecalculation = async () => {
  if (!accessToken) return;
  if (!reading?.rowKey) return;
  if (!selectedModel) return;

  try {
    setUpdatingReading(true);

    const response = await RunModelsBatch(
      accessToken,
      selectedModel,
      [reading.rowKey]
    );

    setRecalculatedReading(
      response.data?.[0] ?? null
    );
  } catch (err) {
    console.error(err);

    alert(intl.get("alerts.recalculateError"));
  } finally {
    setRecalculating(false);
  }
};

const handleSaveResult = async () => {
  if (!accessToken) return;
  if (!user) return;
  if (!recalculatedReading) return;

  try {
    setRecalculating(true);

    await UpdateResults(
      accessToken,
      user,
      [recalculatedReading]
    );

    alert(intl.get("alerts.updateSuccess"));

    onClose();
  } catch (err) {
    console.error(err);

    alert(intl.get("alerts.updateError"));
  } finally {
    setUpdatingReading(false);
  }
};


const handleUpdateReading = async () => {
  if (!accessToken) return;
  if (!editedReading) return;
  if (!reading) return;

  try {
    setUpdatingReading(true);

    const updatedReading: Reading = {
      ...editedReading,
      name: isAverageReading(reading)
        ? getFinalReadingName(editedReading.name)
        : editedReading.name,
    };

    await UpdateReading(accessToken, [updatedReading]);

    alert(intl.get("alerts.updateSuccess"));

    onClose();
  } catch (err) {
    console.error(err);
    alert(intl.get("alerts.updateError"));
  } finally {
    setUpdatingReading(false);
  }
};

interface PredictedValue {
  ParameterName: string;
  Value: number;
  ReferenceValue?: number;
}

const parsePredictedValues = (
  predictedValues?: string
): PredictedValue[] => {
  if (!predictedValues) return [];

  try {
    const parsed = JSON.parse(predictedValues);

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

interface RecalculationInfo {
  User: string;
  Date: string;
  PreviousApplication: string;
  CurrentApplication: string;
}

  const resultRows = React.useMemo(() => {
    if (!reading) return [];

    const original = parsePredictedValues(reading.predictedValues) ?? [];
    const recalculated = recalculatedReading
      ? parsePredictedValues(recalculatedReading.predictedValues)
      : [];

    const rows = original.map((item) => {
      const recalculatedValue = recalculated.find(
        p =>
          p.ParameterName.toLowerCase() ===
          item.ParameterName.toLowerCase()
      );

      return {
        parameterName: item.ParameterName,
        result:
          item.Value != null
            ? Number(item.Value.toFixed(2))
            : "-",
        referenceValue:
          item.ReferenceValue != null
            ? Number(item.ReferenceValue.toFixed(2))
            : "-",
        recalculatedValue:
          recalculatedValue?.Value != null
            ? Number(recalculatedValue.Value.toFixed(2))
            : "-",
      };
    });

    rows.sort((a, b) => {
      const indexA = desiredResultOrder.indexOf(a.parameterName);
      const indexB = desiredResultOrder.indexOf(b.parameterName);

      return (
        (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
        (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
      );
    });

    return rows;
  }, [reading, recalculatedReading]);

  const resultColumns = React.useMemo(() => {
  const columns: any[] = [
    {
      title: "Nutriente",
      field: "parameterName",
      filtering: false,
    },
    {
      title: "Resultado Nutroscan",
      field: "result",
      filtering: false,
    },
    {
      title: "Laboratório",
      field: "referenceValue",
      filtering: false,
    },
  ];

  if (recalculatedReading) {
    columns.push({
      title: "Resultado Recalculado",
      field: "recalculatedValue",
      filtering: false,
    });
  }

  return columns;
}, [recalculatedReading]);

  const formatDate = (value?: string | Date | null) => {
    if (!value) return "-";

    return new Date(value).toLocaleString("pt-BR");
  };

  const recalculationInfo = React.useMemo<RecalculationInfo | null>(() => {
    if (!reading?.recalculationInfo) {
      return null;
    }

    try {
      return JSON.parse(reading.recalculationInfo);
    } catch {
      return null;
    }
  }, [reading]);

  useEffect(() => {
  if (!open) {
    setSelectedModel("");
    setRecalculatedReading(null);
    setSelectedTab(0);
  }
}, [open]);

  const AVERAGE_SUFFIX = "- Média";
  const CULTURE_REQUIRED_DATE = new Date(2026, 8, 1); // 01/09/2026


  const isAverageReading = (reading?: Reading | null) => {
    if (!reading) return false;

  // Related nunca é considerado média
  if (reading.type === "Related") return false;

  const sampleName = String(reading.name ?? "").trim();
  const hasMediaInName = /m[eé]dia/i.test(sampleName);

  // Acquisition
  if (reading.type === "acquisition") {
    const culture = String(reading.culture ?? "").trim();
    const isLegacyAcquisition =
      new Date(reading.dateTime) < CULTURE_REQUIRED_DATE && !culture;

    // Legado sem cultura:
    // só é média se o nome indicar "Média"
    if (isLegacyAcquisition) {
      return hasMediaInName;
    }

    const hasCollectTime = parseCollectDate(reading) !== null;

    // Acquisition normal:
    // collectTime OU "Média" no nome
    return hasCollectTime || hasMediaInName;
  }

  // Outros tipos:
  // somente o nome determina se é média
  return hasMediaInName;
};

const getEditableAverageName = (name?: string | null) => {
  if (!name) return "";

  return name
    .replace(/\s*-\s*m[eé]dia\s*$/i, "")
    .trim();
};

const getFinalReadingName = (name?: string | null) => {
  if (!name) return "";

  return `${name.trim()} ${AVERAGE_SUFFIX}`;
};

function parseCollectDate(r: Reading): Date | null {
  if (!r.collectTime || String(r.collectTime).trim() === "") return null;
  try {
    const raw = String(r.collectTime).trim();
    if (raw.includes("/")) {
      const [datePart, timePart] = raw.split(" ");
      const [day, month, year] = datePart.split("/").map(Number);
      let h = 0,
        m = 0,
        s = 0;
      if (timePart) {
        const t = timePart.split(":");
        h = Number(t[0]) || 0;
        m = Number(t[1]) || 0;
        s = Number(t[2]) || 0;
      }
      return new Date(year, month - 1, day, h, m, s);
    }
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
       PaperProps={{
        style: {
          width: "1100px",
          height: "650px",
          maxWidth: "1100px",
          maxHeight: "750px",
        },
      }}
    >
      <DialogTitle>
        Detalhes da Leitura
      </DialogTitle>

      <DialogContent>
        {recalculating || updatingReading ? (
          <div
            style={{
              height: 400,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <SpinLoading />
             <div>
              {updatingReading
                ? "Atualizando informações..."
                : "Recalculando leitura..."}
            </div>
          </div>
        ) : (
          <>
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Dados da Amostra" />
          <Tab label="Resultados" />
          <Tab label="Recálculo" />
          <Tab label="Histórico" />
        </Tabs>
        {selectedTab === 0 && (
          <>
            <Grid container spacing={2} style={{ marginTop: 8 }}>

              {/* Nome da Amostra */}
              <Grid item xs={12} md={4}>
                <div
                  style={{
                    marginBottom: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: '#666',
                    }}
                  >
                    Nome da Amostra
                  </span>

                  <IconButton
                    size="small"
                    onClick={() =>
                      setEditingField(
                        editingField === 'name' ? null : 'name'
                      )
                    }
                  >
                    {editingField === 'name' ? (
                      <CheckIcon fontSize="small" />
                    ) : (
                      <EditIcon fontSize="small" />
                    )}
                  </IconButton>
                </div>

                {editingField === 'name' ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={editedReading?.name ?? ''}
                      onChange={(e) =>
                        setEditedReading(prev =>
                          prev
                            ? {
                                ...prev,
                                name: e.target.value,
                              }
                            : prev
                        )
                      }
                      InputProps={
                        isAverageReading(reading)
                          ? {
                              endAdornment: (
                                <span
                                  style={{
                                    color: '#666',
                                    whiteSpace: 'nowrap',
                                    marginLeft: 8,
                                    fontWeight: 500,
                                  }}
                                >
                                  {AVERAGE_SUFFIX}
                                </span>
                              ),
                            }
                          : undefined
                      }
                    />
                  </div>
                ) : (
                  <div>
                    {isAverageReading(reading)
                      ? `${getEditableAverageName(editedReading?.name)} ${AVERAGE_SUFFIX}`
                      : editedReading?.name || '-'}
                  </div>
                )}
              </Grid>

              {/* Código da Amostra */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Código da Amostra
                  </span>
                </div>
                <div>{reading?.sampleCode || '-'}</div>
              </Grid>

              {/* Cliente */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Cliente
                  </span>
                  <IconButton
                    size='small'
                    onClick={() => setEditingField(editingField === 'customerName' ? null : 'customerName')}
                  >
                    {editingField === 'customerName' ? (
                      <CheckIcon fontSize='small' />
                    ) : (
                      <EditIcon fontSize='small' />
                    )}
                  </IconButton>
                </div>

                {editingField === 'customerName' ? (
                  <TextField
                    fullWidth
                    size='small'
                    variant='outlined'
                    value={editedReading?.customerName ?? ''}
                    onChange={(e) =>
                      setEditedReading(prev =>
                        prev ? { ...prev, customerName: e.target.value } : prev
                      )
                    }
                  />
                ) : (
                  <div>{editedReading?.customerName || '-'}</div>
                )}
              </Grid>

              {/* Usuário */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Usuário
                  </span>
                </div>
                <div>{reading?.userName || '-'}</div>
              </Grid>

              {/* Cultura */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Cultura
                  </span>
                </div>
                <div>
                  {reading?.culture != null
                    ? cultureLabels[Number(reading.culture) as keyof typeof cultureLabels] ?? "-"
                    : "-"}
                </div>
              </Grid>

              {/* Cultivar */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Cultivar
                  </span>
                  <IconButton
                    size='small'
                    onClick={() => setEditingField(editingField === 'cultivate' ? null : 'cultivate')}
                  >
                    {editingField === 'cultivate' ? (
                      <CheckIcon fontSize='small' />
                    ) : (
                      <EditIcon fontSize='small' />
                    )}
                  </IconButton>
                </div>

                {editingField === 'cultivate' ? (
                  <TextField
                    fullWidth
                    size='small'
                    variant='outlined'
                    value={editedReading?.cultivate ?? ''}
                    onChange={(e) =>
                      setEditedReading(prev =>
                        prev ? { ...prev, cultivate: e.target.value } : prev
                      )
                    }
                  />
                ) : (
                  <div>{editedReading?.cultivate || '-'}</div>
                )}
              </Grid>
              {/* Tipo de Cana */}
              <Grid item xs={12} md={4}>
                <div
                  style={{
                    marginBottom: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#666",
                    }}
                  >
                    Tipo de Cana
                  </span>

                  <IconButton
                    size="small"
                    onClick={() =>
                      setEditingField(
                        editingField === "sugarcaneType"
                          ? null
                          : "sugarcaneType"
                      )
                    }
                  >
                    {editingField === "sugarcaneType" ? (
                      <CheckIcon fontSize="small" />
                    ) : (
                      <EditIcon fontSize="small" />
                    )}
                  </IconButton>
                </div>

                {editingField === "sugarcaneType" ? (
                  <FormControl
                    fullWidth
                    size="small"
                    variant="outlined"
                  >
                    <Select
                      value={editedReading?.sugarcaneType ?? ""}
                      onChange={(e) =>
                        setEditedReading(prev =>
                          prev
                            ? {
                                ...prev,
                                sugarcaneType:
                                  e.target.value === ""
                                    ? undefined
                                    : Number(e.target.value),
                              }
                            : prev
                        )
                      }
                    >
                      <MenuItem value="">
                        <em>Selecione...</em>
                      </MenuItem>

                      <MenuItem value={0}>Colmo</MenuItem>
                      <MenuItem value={1}>Folha</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <div>
                    {editedReading?.sugarcaneType != null
                      ? sugarcaneTypeLabels[editedReading.sugarcaneType] ?? "-"
                      : "-"}
                  </div>
                )}
              </Grid>
                {/* Cultura Personalizada */}
                <Grid item xs={12} md={4}>
                  <div
                    style={{
                      marginBottom: 4,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#666",
                      }}
                    >
                      Cultura Personalizada
                    </span>

                    <IconButton
                      size="small"
                      onClick={() =>
                        setEditingField(
                          editingField === "customCultureName"
                            ? null
                            : "customCultureName"
                        )
                      }
                    >
                      {editingField === "customCultureName" ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <EditIcon fontSize="small" />
                      )}
                    </IconButton>
                  </div>

                  {editingField === "customCultureName" ? (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={editedReading?.customCultureName ?? ""}
                      onChange={(e) =>
                        setEditedReading(prev =>
                          prev
                            ? {
                                ...prev,
                                customCultureName: e.target.value,
                              }
                            : prev
                        )
                      }
                    />
                  ) : (
                    <div>{editedReading?.customCultureName || "-"}</div>
                  )}
                </Grid>

              {/* Estádio Fenológico */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Estádio Fenológico
                  </span>
                </div>
                <div>{reading?.growthStage || '-'}</div>
              </Grid>

              {/* Terço da Planta */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Terço da Planta
                  </span>
                </div>
                <div>{getPlantThirdLabel(reading?.plantThird)}</div>
              </Grid>

              {/* Talhão */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Talhão
                  </span>
                  <IconButton
                    size='small'
                    onClick={() => setEditingField(editingField === 'plot' ? null : 'plot')}
                  >
                    {editingField === 'plot' ? (
                      <CheckIcon fontSize='small' />
                    ) : (
                      <EditIcon fontSize='small' />
                    )}
                  </IconButton>
                </div>

                {editingField === 'plot' ? (
                  <TextField
                    fullWidth
                    size='small'
                    variant='outlined'
                    value={editedReading?.plot ?? ''}
                    onChange={(e) =>
                      setEditedReading(prev =>
                        prev ? { ...prev, plot: e.target.value } : prev
                      )
                    }
                  />
                ) : (
                  <div>{editedReading?.plot || '-'}</div>
                )}
              </Grid>

              {/* Identificador de Campo */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Identificador de Campo
                  </span>
                  <IconButton
                    size='small'
                    onClick={() => setEditingField(editingField === 'fieldIdentifier' ? null : 'fieldIdentifier')}
                  >
                    {editingField === 'fieldIdentifier' ? (
                      <CheckIcon fontSize='small' />
                    ) : (
                      <EditIcon fontSize='small' />
                    )}
                  </IconButton>
                </div>

                {editingField === 'fieldIdentifier' ? (
                  <TextField
                    fullWidth
                    size='small'
                    variant='outlined'
                    value={editedReading?.fieldIdentifier ?? ''}
                    onChange={(e) =>
                      setEditedReading(prev =>
                        prev ? { ...prev, fieldIdentifier: e.target.value } : prev
                      )
                    }
                  />
                ) : (
                  <div>{editedReading?.fieldIdentifier || '-'}</div>
                )}
              </Grid>

              {/* Aplicação */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Aplicação
                  </span>
                </div>
                <div>{reading?.type || '-'}</div>
              </Grid>

              {/* Data e Hora */}
              <Grid item xs={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>
                    Data e Hora
                  </span>
                </div>
                <div>{reading?.dateTime || '-'}</div>
              </Grid>

            </Grid>

            <Grid
              container
              justifyContent='flex-end'
              style={{ marginTop: 16 }}
            >
              <Button
                variant='contained'
                color='primary'
                onClick={handleUpdateReading}
                disabled={updatingReading}
              >
                {updatingReading ? "Atualizando leitura..." : "Atualizar Leitura"}
              </Button>
            </Grid>
          </>
        )}
        {selectedTab === 1 && (
          <MuiThemeProvider theme={createTheme()}>
            <MaterialTable
              columns={resultColumns}
              data={resultRows}
              options={{
                search: false,
                filtering: false,
                paging: false,
                toolbar: false,
                showTitle: false,
                sorting: false,
              }}
            />
          </MuiThemeProvider>
        )}
        {selectedTab === 2 && (
          <Grid container direction="column" spacing={3} style={{ marginTop: 8 }}>
            <Grid item md={10}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  marginBottom: 16,
                }}
              >
                Informações de Recálculo
              </div>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs>
                  <div style={{ marginBottom: 8 }}>
                    Versão da calibração
                  </div>

                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Aplicação</InputLabel>

                    <Select
                      value={selectedModel}
                      onChange={(e) =>
                        setSelectedModel(e.target.value as string)
                      }
                      label="Aplicação"
                    >
                      <MenuItem value="">
                        <em>
                          {modelsLoading
                            ? "Carregando aplicações..."
                            : "Selecione uma aplicação"}
                        </em>
                      </MenuItem>

                      {filteredModels.map((model) => (
                        <MenuItem
                          key={model.rowKey}
                          value={model.applicationName}
                        >
                          {model.applicationName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item style={{ marginTop: 30 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!selectedModel || recalculating}
                    onClick={handlePreviewRecalculation}
                  >
                    Visualizar recálculo:
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <MuiThemeProvider theme={createTheme()}>
                <MaterialTable
                  columns={resultColumns}
                  data={resultRows}
                  options={{
                    search: false,
                    filtering: false,
                    paging: false,
                    toolbar: false,
                    showTitle: false,
                    sorting: false,
                  }}
                />
              </MuiThemeProvider>
            </Grid>

            <Grid item>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!recalculatedReading || recalculating}
                  onClick={handleSaveResult}
                >
                  Salvar Resultado
                </Button>
              </div>
            </Grid>
          </Grid>
        )}
        {selectedTab === 3 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <strong>Última atualização:</strong>{" "}
              {formatDate(reading?.syncedToCloudAt)}
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Leitura realizada em:</strong>{" "}
              {reading?.dateTime ?? "-"}
            </div>

            {recalculationInfo && (
              <div style={{ marginBottom: 16 }}>
                <div>
                  <strong>Recálculo realizado em:</strong>{" "}
                  {formatDate(recalculationInfo.Date)}
                  {" pelo usuário "}
                  <strong>{recalculationInfo.User}</strong>{"."}
                </div>

                <div style={{ marginTop: 4, marginLeft: 6 }}>
                  <strong>Aplicação anterior:</strong>{" "}
                  {recalculationInfo.PreviousApplication}
                </div>

                <div style={{ marginLeft: 6 }}>
                  <strong>Aplicação atual:</strong>{" "}
                  {recalculationInfo.CurrentApplication}
                 </div>
            </div>
          )}
        </div>
      )}
    </>
  )}
</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {intl.get("recalculateModal.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}